/**
 * "Turn on AI editor" — THE one flow that takes a merchant into Shopify's
 * theme editor with the Autonoma app embed switched on. Used by the
 * Settings card, the dashboard embed nudge and the bell entry, so every
 * entry point behaves the same. Everything that needs the click's user
 * activation happens SYNCHRONOUSLY inside the handler, before any await:
 *
 *   1. copy the current connect code to the clipboard (execCommand first
 *      — synchronous — then navigator.clipboard)
 *   2. open a NEW TAB at the theme editor (App Bridge patches window.open,
 *      so `_blank` from the embedded app opens a real browser tab). The
 *      URL comes pre-fetched from GET /status (`newTabUrl`, activateAppId
 *      only while the embed is off); without it we open after /turn-on.
 *   3. POST /api/theme-editor-admin/turn-on { themeId? } → ai_editor_enabled
 *      = true, the theme's embed state, the definitive URL and the code
 *   4. toast: "Code copied — if the editor asks, just paste it." (or "Copy
 *      the code below" when the clipboard was blocked)
 *
 * The panel inside the editor then connects by itself (remembered
 * browser → render-time ticket → link code) and, failing all of those,
 * shows the paste box — hence the copied code. Never `_top`, never a
 * popup from the preview.
 */
import { apiFetch } from './api';

export const TURN_ON_LABEL = 'Turn on AI editor';
export const OPEN_LABEL = 'Open AI editor';
export const COPIED_TOAST = 'Code copied — if the editor asks, just paste it.';
export const COPY_BELOW_TOAST = 'Copy the code below';
export const SAVE_TOAST = 'Click Save (top right) to finish';
const TOAST_ID = 'autonoma-theme-editor-toast';

/** A fixed toast on OUR page (the editor opens in another tab, so this one stays visible). */
export function showTurnOnToast(message) {
  if (typeof document === 'undefined') return;
  try {
    document.getElementById(TOAST_ID)?.remove();
    const el = document.createElement('div');
    el.id = TOAST_ID;
    el.setAttribute('role', 'status');
    el.setAttribute('data-testid', 'theme-editor-toast');
    el.textContent = message;
    el.style.cssText = 'position:fixed;top:18px;left:50%;transform:translateX(-50%);z-index:10600;max-width:520px;background:#1a1a2e;color:#fff;font:600 14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:12px 18px;border-radius:12px;box-shadow:0 12px 34px rgba(30,20,80,.28)';
    document.body.appendChild(el);
    setTimeout(() => { try { el.remove(); } catch { /* gone */ } }, 6000);
  } catch { /* toast is cosmetic */ }
}

/**
 * Copy text inside a click handler. execCommand('copy') is synchronous and
 * keeps working after the activation window closes; navigator.clipboard is
 * the modern path. Resolves true when either worked.
 */
export function copyText(text) {
  const value = String(text == null ? '' : text);
  if (!value) return Promise.resolve(false);
  let sync = false;
  try {
    if (typeof document !== 'undefined' && document.execCommand) {
      const ta = document.createElement('textarea');
      ta.value = value; ta.setAttribute('readonly', ''); ta.setAttribute('aria-hidden', 'true');
      ta.style.cssText = 'position:fixed;top:-1000px;left:-1000px;opacity:0';
      document.body.appendChild(ta);
      ta.select(); ta.setSelectionRange(0, value.length);
      sync = !!document.execCommand('copy');
      ta.remove();
    }
  } catch { sync = false; }
  const modern = (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText)
    ? navigator.clipboard.writeText(value).then(() => true, () => false)
    : Promise.resolve(false);
  return modern.then((ok) => ok || sync);
}

/**
 * Open a new tab. Under App Bridge `window.open(url, '_blank')` is proxied
 * to the admin, which opens a real tab (and returns null); outside the
 * admin it is a plain new tab. Returns whatever window.open returned.
 */
export function openNewTab(url) {
  if (!url || typeof window === 'undefined') return null;
  try { return window.open(url, '_blank', 'noopener'); } catch { return null; }
}

/**
 * Run the flow from a click handler. `prefetched` is the card's current
 * GET /status payload (`newTabUrl`, `pairing.code`) so the tab and the
 * clipboard can be handled before the first await. Resolves with the
 * backend payload plus { copied, opened }; rejects with an Error carrying
 * `code` / `status` when the backend refuses (plan gate, unknown theme…).
 */
export async function turnOnAiEditor({ themeId = null, navigate = true, prefetched = null } = {}) {
  const preUrl = prefetched?.newTabUrl || null;
  const preCode = prefetched?.pairing?.code || prefetched?.code || null;
  const copying = preCode ? copyText(preCode) : null;
  let opened = false;
  if (navigate && preUrl) { openNewTab(preUrl); opened = true; } // null from App Bridge still means "asked the admin to open it"

  const res = await apiFetch('/api/theme-editor-admin/turn-on', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(themeId ? { themeId: String(themeId) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || 'Could not turn on the AI editor.');
    err.code = data.error; err.status = res.status; err.data = data;
    throw err;
  }
  let copied = copying ? await copying : false;
  if (!copied && data.code?.code) copied = await copyText(data.code.code);
  if (navigate && data.url) {
    // No pre-fetched URL, or the embed state changed since /status (the
    // activateAppId part differs): open (again) with the definitive URL.
    if (!preUrl || /activateAppId/.test(data.url) !== /activateAppId/.test(preUrl)) { openNewTab(data.url); opened = true; }
  }
  if (navigate) showTurnOnToast(copied ? COPIED_TOAST : COPY_BELOW_TOAST);
  return { ...data, copied, opened };
}

/** "3 minutes ago" / "yesterday" for the card's "last used" line. */
export function relativeTime(iso, now = Date.now()) {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return null;
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60); if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24); if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  return new Date(t).toLocaleDateString();
}

/** "9:42" for the "New code in m:ss" countdown. */
export function countdownText(expiresAt, now = Date.now()) {
  const t = expiresAt ? Date.parse(expiresAt) : NaN;
  if (!Number.isFinite(t)) return null;
  const s = Math.max(0, Math.floor((t - now) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
